import {useCallback, useEffect, useState} from 'react';
import HomeSectionsService from '../services/HomeSectionsService';
import {useUserStore} from '../store/UserStore';

export type HomeSection =
  | {kind: 'featured'; products: any[] | null}
  | {kind: 'suggested'; products: any[] | null}
  | {
      kind: 'carousel';
      id: number;
      type: 'category' | 'group';
      displayName: string;
      categoryNames: string[];
      load: 'eager' | 'lazy';
      products: any[] | null;
    };

interface UseHomeSectionsResult {
  sections: HomeSection[];
  loading: boolean;
  error: string | null;
  refresh: (force?: boolean) => Promise<void>;
  lazyLoad: (id: number) => void;
}

const buildSections = (data: any): HomeSection[] => {
  if (!data) return [];

  const sections: HomeSection[] = [];

  if (data.featured !== undefined) {
    const products = data.featured?.products ?? data.featured ?? null;
    sections.push({kind: 'featured', products});
  }

  if (data.suggested !== undefined) {
    const rawProducts = data.suggested?.products ?? data.suggested ?? null;
    const products = Array.isArray(rawProducts)
      ? rawProducts.map((obj: any) =>
          Object.fromEntries(
            Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]),
          ),
        )
      : rawProducts;
    sections.push({kind: 'suggested', products});
  }

  if (Array.isArray(data.carousels)) {
    data.carousels.forEach((carousel: any, index: number) => {
      sections.push({
        kind: 'carousel',
        id: carousel.id,
        type: carousel.type ?? 'category',
        displayName: carousel.display_name ?? carousel.displayName ?? '',
        categoryNames: carousel.category_names ?? carousel.categoryNames ?? [],
        load: carousel.load ?? (index < 2 ? 'eager' : 'lazy'),
        products: carousel.products ?? null,
      });
    });
  }

  return sections;
};

const INITIAL_SECTIONS: HomeSection[] = [
  {kind: 'featured', products: null},
  {kind: 'suggested', products: null},
];

export const useHomeSections = (): UseHomeSectionsResult => {
  const {user} = useUserStore();
  const [sections, setSections] = useState<HomeSection[]>(INITIAL_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clientCode =
    user?.current_client?.clientCode || user?.clientCode || '';

  const fetchSections = useCallback(
    async (force = false) => {
      if (!clientCode) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await HomeSectionsService.getHomeSections(clientCode, {
          fresh: force,
        });
        setSections(buildSections(data));
      } catch (err: any) {
        setError(err?.message ?? 'Erro ao carregar seções');
        setSections([]);
      } finally {
        setLoading(false);
      }
    },
    [clientCode],
  );

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const refresh = useCallback(
    (force = false) => fetchSections(force),
    [fetchSections],
  );

  const lazyLoad = useCallback(
    async (id: number) => {
      if (!clientCode) return;
      try {
        const data = await HomeSectionsService.getCarouselProducts(
          clientCode,
          id,
        );
        const products: any[] = data?.products ?? data ?? [];
        setSections(prev =>
          prev.map(section => {
            if (section.kind === 'carousel' && section.id === id) {
              return {...section, products};
            }
            return section;
          }),
        );
      } catch (err) {
        setSections(prev =>
          prev.map(section => {
            if (section.kind === 'carousel' && section.id === id) {
              return {...section, products: []};
            }
            return section;
          }),
        );
      }
    },
    [clientCode],
  );

  return {sections, loading, error, refresh, lazyLoad};
};
